import { User, Training } from "../types";

/**
 * Check if a user can manage (edit/update) a training record
 */
export const canUserManageTraining = (
  currentUser: User | null,
  trainingUserId: string,
  users: User[]
): boolean => {
  if (!currentUser) return false;

  // Admin and Director can manage all training
  if (currentUser.userRole === 'Admin' || currentUser.userRole === 'Director') {
    return true;
  }

  // Manager can manage their own training and training of their direct reports
  if (currentUser.userRole === 'Manager' || currentUser.userRole === 'Senior Manager') {
    // Own training
    if (currentUser.id === trainingUserId) return true;

    // Check if this user reports to the manager
    const user = users.find(u => u.id === trainingUserId);
    return !!user && user.manager === currentUser.id;
  }

  // Worker can only manage their own training
  return currentUser.id === trainingUserId;
};

/**
 * Check if a user can change training status based on role and current/new status
 * Worker permissions:
 * - Can change from Not Started -> In Progress
 * - Can change from Not Started -> Completed
 * - Cannot change from In Progress -> Not Started
 * - Cannot change from Completed -> anything else
 * 
 * Admin/Director/Manager permissions:
 * - Can change between any status
 */
export const canChangeTrainingStatus = (
  currentUser: User | null,
  currentStatus: string,
  newStatus: string
): boolean => {
  if (!currentUser) return false;

  // Admin, Director, and Manager/Senior Manager can change between any status
  if (currentUser.userRole === 'Admin' ||
    currentUser.userRole === 'Director' ||
    currentUser.userRole === 'Manager' ||
    currentUser.userRole === 'Senior Manager') {
    return true;
  }

  // Worker permission rules
  if (currentUser.userRole === 'Worker') {
    // Cannot change FROM Completed status to anything
    if (currentStatus === 'Completed') {
      return false;
    }

    // Cannot change FROM In Progress TO Not Started (backward)
    if (currentStatus === 'In Progress' && newStatus === 'Not Started') {
      return false;
    }

    // Can change FROM Not Started TO In Progress or Completed (forward)
    return true;
  }

  return false;
};

/**
 * Check if a user can view a training record
 */
export const canUserViewTraining = (
  currentUser: User | null,
  trainingUserId: string,
  users: User[]
): boolean => {
  return canUserManageTraining(currentUser, trainingUserId, users);
};

/**
 * Get a list of users that the current user is allowed to see training records for
 */
export const getUsersVisibleTocurrentUser = (
  currentUser: User | null,
  allUsers: User[]
): User[] => {
  if (!currentUser) return [];

  switch (currentUser.userRole) {
    case 'Admin':
    case 'Director':
      return allUsers;

    case 'Senior Manager':
    case 'Manager':
      // Return the manager and all their direct reports
      return allUsers.filter(
        user => user.id === currentUser.id || user.manager === currentUser.id
      );

    case 'Worker':
    default:
      // Return only the worker themselves
      return allUsers.filter(user => user.id === currentUser.id);
  }
};

/**
 * Filter trainings based on user permissions
 */
export const getTrainingsVisibleToUser = (
  currentUser: User | null,
  allTrainings: Training[],
  allUsers: User[]
): Training[] => {
  if (!currentUser) return [];

  // Get visible users first
  const visibleUsers = getUsersVisibleTocurrentUser(currentUser, allUsers);
  const visibleUserIds = visibleUsers.map(u => u.id);

  // Filter trainings to only show those for visible users
  return allTrainings.filter(training => visibleUserIds.includes(training.userId));
}; 